// src/pages/BlogPostDetail.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Avatar,
  Alert,
  AppBar,
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Toolbar,
  Typography,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogContent,
  Link,
  Slide,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbUpOffAltRoundedIcon from "@mui/icons-material/ThumbUpOffAltRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import api from "../lib/api";
import AppSnackbar from "../components/AppSnackbar";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { BLOG_FALLBACK_IMAGE } from "../components/BlogPostCard";
import { BLOG_CATEGORY_GROUPS, getCategoryStyle } from "../utils/blogTaxonomy";
import {
  IMAGE_UPLOAD_SIZE_ERROR,
  imageExceedsUploadLimit,
} from "../utils/imageUpload";
import { authFieldSx } from "./authFieldStyles";

const EditPostDialogTransition = React.forwardRef(function EditPostDialogTransition(
  props,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const editPanelSx = {
  p: { xs: 2.5, md: 3 },
  bgcolor: "#fff",
  borderRadius: 2,
};

const tagAutocompletePaperProps = {
  sx: {
    bgcolor: "#fff",
    border: "1px solid #e8e8e8",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    "& .MuiAutocomplete-listbox": { bgcolor: "#fff" },
    "& .MuiAutocomplete-option": {
      backgroundColor: "#fff !important",
      "&:hover, &.Mui-focused": {
        backgroundColor: "#fffaf0 !important",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "#fff7d6 !important",
      },
      '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
        backgroundColor: "#ffefb0 !important",
      },
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
        },
        "&.Mui-selected": {
          backgroundColor: "#fff7d6 !important",
        },
        "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
        },
      },
    },
  },
};

export default function BlogPostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [post, setPost] = useState(null);
  const [moreArticles, setMoreArticles] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedReplyThreads, setExpandedReplyThreads] = useState({});
  const [replyContent, setReplyContent] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [likingCommentIds, setLikingCommentIds] = useState({});
  const [deletePostDialogOpen, setDeletePostDialogOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState(false);
  const replyInputRef = useRef(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editExcerpt, setEditExcerpt] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [editFeaturedImage, setEditFeaturedImage] = useState(null);
  const [editExistingImageUrl, setEditExistingImageUrl] = useState(null);
  const [editImageRemoved, setEditImageRemoved] = useState(false);
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogTags, setBlogTags] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ αυτό είναι το username/email που θα χρησιμοποιήσουμε στο /u/:username
  const [authorUsername, setAuthorUsername] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const editImagePreview = useMemo(
    () => (editFeaturedImage ? URL.createObjectURL(editFeaturedImage) : null),
    [editFeaturedImage]
  );

  useEffect(() => {
    fetchPost();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  useEffect(() => {
    if (!location.hash || comments.length === 0) return;
    const targetId = location.hash.replace("#comment-", "");
    if (!targetId) return;
    const numericTargetId = Number(targetId);

    const parentWithTargetReply = comments.find((comment) =>
      (comment.replies || []).some((reply) => reply.id === numericTargetId)
    );

    if (parentWithTargetReply) {
      setExpandedReplyThreads((prev) =>
        prev[parentWithTargetReply.id]
          ? prev
          : { ...prev, [parentWithTargetReply.id]: true }
      );
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(`comment-${targetId}`);
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedCommentId(numericTargetId);
      window.setTimeout(() => setHighlightedCommentId(null), 3000);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [location.hash, comments]);

  useEffect(() => {
    if (!replyingTo) return undefined;
    const timer = window.setTimeout(() => {
      replyInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [replyingTo]);

  const tryExtractUsername = (data) => {
    // προσπάθησε να βγάλεις username/email από διάφορα πιθανά shapes
    return (
      data?.username ||
      data?.email ||
      data?.user?.username ||
      data?.user?.email ||
      data?.profile?.username ||
      data?.profile?.email ||
      data?.account?.username ||
      data?.account?.email ||
      ""
    );
  };

  const resolveAuthorUsernameFromId = useCallback(async (authorId) => {
    if (!authorId) return "";

    // ⚠️ Εδώ είναι το κρίσιμο σημείο:
    // Βάλε/κράτα μόνο τα endpoints που ΥΠΑΡΧΟΥΝ στον backend σου.
    const candidates = [
      `/profile/id/${authorId}/`,
      `/profile/${authorId}/`,
      `/users/${authorId}/`,
      `/users/${authorId}/profile/`,
      `/accounts/users/${authorId}/`,
    ];

    for (const url of candidates) {
      try {
        const r = await api.get(url);
        const u = tryExtractUsername(r.data);
        if (u) return u;
      } catch (e) {
        // δοκίμασε το επόμενο candidate
      }
    }

    return "";
  }, []);

  const fetchPost = async () => {
    try {
      const res = await api.get(`/blog/posts/${postId}`);
      console.log("POST DATA =>", JSON.stringify(res.data, null, 2));

      setPost(res.data);
      const canonicalPostId = String(res.data?.id || "");
      if (canonicalPostId && String(postId) !== canonicalPostId) {
        navigate(`/blog/${canonicalPostId}${location.hash || ""}`, {
          replace: true,
        });
      }
      fetchMoreArticles(res.data);

      // 1) Αν ο backend κάποτε στείλει author_username, το παίρνουμε άμεσα
      const direct =
        res.data?.author_username ||
        res.data?.author?.username ||
        res.data?.author?.email ||
        "";

      if (direct) {
        setAuthorUsername(direct);
        return;
      }

      // 2) Αλλιώς προσπαθούμε να το κάνουμε resolve από author_id
      if (res.data?.author_id) {
        const resolved = await resolveAuthorUsernameFromId(res.data.author_id);
        setAuthorUsername(resolved || "");
      } else {
        setAuthorUsername("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMoreArticles = async (currentPost) => {
    try {
      const res = await api.get("/blog/posts/", {
        params: { page_size: 12, ordering: "-published_at" },
      });
      const articles = (res.data?.results || []).filter(
        (article) => article.slug !== currentPost.slug
      );
      const sameCategory = articles.filter(
        (article) => article.category && article.category === currentPost.category
      );
      const sameAuthor = articles.filter(
        (article) =>
          article.author_username &&
          article.author_username === currentPost.author_username &&
          !sameCategory.some((item) => item.slug === article.slug)
      );
      setMoreArticles([...sameCategory, ...sameAuthor].slice(0, 3));
    } catch (err) {
      console.error(err);
      setMoreArticles([]);
    }
  };

  const orderRepliesForThread = (replies = [], parentId) => {
    const sortedReplies = [...replies].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    const getReplyTargetId = (reply) => {
      const target = reply?.reply_to_id ?? reply?.reply_to;
      if (!target) return "";
      return String(
        typeof target === "object" ? target?.id : target
      );
    };
    const parentKey = String(parentId);
    const replyIds = new Set(sortedReplies.map((reply) => String(reply.id)));
    const childrenByTarget = new Map();
    const roots = [];

    sortedReplies.forEach((reply) => {
      const replyToId = getReplyTargetId(reply);

      if (replyToId && replyToId !== parentKey && replyIds.has(replyToId)) {
        const currentChildren = childrenByTarget.get(replyToId) || [];
        currentChildren.push(reply);
        childrenByTarget.set(replyToId, currentChildren);
      } else {
        roots.push(reply);
      }
    });

    childrenByTarget.forEach((children, targetId) => {
      childrenByTarget.set(
        targetId,
        [...children].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    });

    const ordered = [];
    const visited = new Set();
    const appendReplyWithChildren = (reply) => {
      const replyKey = String(reply?.id || "");
      if (!replyKey || visited.has(replyKey)) return;
      visited.add(replyKey);
      ordered.push(reply);
      (childrenByTarget.get(replyKey) || []).forEach(appendReplyWithChildren);
    };

    roots.forEach(appendReplyWithChildren);
    sortedReplies.forEach(appendReplyWithChildren);
    return ordered;
  };

  const fetchComments = async () => {
    try {
      const res = await api.get(`/blog/posts/${postId}/comments`);
      const orderedComments = (res.data || [])
        .map((comment) => ({
          ...comment,
          replies: orderRepliesForThread(comment.replies || [], comment.id),
        }))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setComments(orderedComments);
      return orderedComments;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const updateCommentLikeState = (commentId, likeData) => {
    setComments((previous) =>
      previous.map((comment) => {
        if (comment.id === commentId) {
          return { ...comment, ...likeData };
        }
        return {
          ...comment,
          replies: (comment.replies || []).map((reply) =>
            reply.id === commentId ? { ...reply, ...likeData } : reply
          ),
        };
      })
    );
  };

  const toggleCommentLike = async (commentId) => {
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: "Sign in to like comments.",
        severity: "info",
      });
      return;
    }
    if (likingCommentIds[commentId]) return;

    setLikingCommentIds((previous) => ({ ...previous, [commentId]: true }));
    try {
      const response = await api.post(`/blog/comments/${commentId}/like/`);
      updateCommentLikeState(commentId, {
        like_count: response.data.like_count,
        liked_by_me: response.data.liked_by_me,
        likers: response.data.likers,
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Could not update this like.",
        severity: "error",
      });
    } finally {
      setLikingCommentIds((previous) => {
        const next = { ...previous };
        delete next[commentId];
        return next;
      });
    }
  };
  useEffect(() => {
    if (post) {
      setEditTitle(post.title);
      setEditExcerpt(post.excerpt || "");
      setEditContent(post.content);
      setEditExistingImageUrl(post.image || null);
      setEditImageRemoved(false);
    }
  }, [post]);

  useEffect(
    () => () => {
      if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    },
    [editImagePreview]
  );

  const submitComment = async (parentId = null, replyToId = null) => {
    const content = parentId ? replyContent : newComment;
    if (!content.trim()) return;

    try {
      await api.post(`/blog/posts/${postId}/comments`, {
        content,
        parent: parentId,
        reply_to: replyToId,
      });

      if (parentId) {
        setReplyContent("");
        setReplyingTo(null);
      } else {
        setNewComment("");
      }
      await fetchComments();
      if (parentId) {
        setExpandedReplyThreads((prev) => ({ ...prev, [parentId]: true }));
      }
    } catch (err) {
      console.error(err);
      alert(parentId ? "Failed to add reply" : "Failed to add comment");
    }
  };

  const openEditPostDialog = async () => {
    setEditDialogOpen(true);
    setEditError("");
    setEditFeaturedImage(null);
    setEditImageRemoved(false);
    setEditTitle(post.title || "");
    setEditExcerpt(post.excerpt || "");
    setEditContent(post.content || "");
    setEditExistingImageUrl(post.image || null);

    try {
      const [categoryRes, tagRes] = await Promise.all([
        api.get("/blog/categories/"),
        api.get("/blog/tags/"),
      ]);
      const realCategories = (categoryRes.data || []).filter(
        (item) => item.id !== "All" && item.name !== "All"
      );
      const availableTags = tagRes.data || [];
      setBlogCategories(realCategories);
      setBlogTags(availableTags);
      setEditCategory(
        typeof post.category === "number"
          ? post.category
          : realCategories.find((item) => item.name === post.category)?.id || ""
      );
      setEditTags(
        Array.isArray(post.tags)
          ? post.tags.map(
              (name) =>
                availableTags.find((item) => item.name === name) || {
                  id: null,
                  name,
                }
            )
          : []
      );
    } catch (err) {
      console.error(err);
      setEditError("Could not load categories and tags.");
    }
  };

  const savePostEdit = async () => {
    setEditError("");
    if (!editTitle.trim()) {
      setEditError("Please add a title.");
      return;
    }
    if (!editContent || editContent === "<p><br></p>") {
      setEditError("Please add the main content.");
      return;
    }
    if (!editCategory) {
      setEditError("Please select a category.");
      return;
    }

    const formData = new FormData();
    formData.append("title", editTitle.trim());
    formData.append("excerpt", editExcerpt.trim());
    formData.append("content", editContent);
    formData.append("status", post.status || "published");
    formData.append("category", editCategory);
    formData.append("clear_tags", "1");
    editTags
      .filter((tag) => tag.id)
      .forEach((tag) => formData.append("tags", tag.id));
    editTags
      .filter((tag) => !tag.id)
      .forEach((tag) => formData.append("new_tags", tag.name));
    if (editFeaturedImage) {
      formData.append("featured_image", editFeaturedImage);
    } else if (editImageRemoved) {
      formData.append("remove_featured_image", "true");
    }

    try {
      setEditLoading(true);
      await api.put(`/blog/posts/${postId}/edit/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditDialogOpen(false);
      await fetchPost();
      setSnackbar({
        open: true,
        message: "The post was updated.",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setEditError(
        err?.response?.data?.error ||
          err?.response?.data?.featured_image?.[0] ||
          err?.response?.data?.title?.[0] ||
          err?.response?.data?.category?.[0] ||
          err?.response?.data?.detail ||
          "Failed to update the post."
      );
    } finally {
      setEditLoading(false);
    }
  };

  const deletePost = async () => {
    try {
      setDeletingPost(true);
      await api.delete(`/blog/posts/${postId}/delete`);
      setDeletePostDialogOpen(false);
      navigate("/blog");
    } catch (err) {
      console.error(err);
      alert("Failed to delete post");
    } finally {
      setDeletingPost(false);
    }
  };

  const startCommentEdit = (id, content) => {
    setEditingCommentId(id);
    setEditingCommentContent(content || "");
    setReplyingTo(null);
    setReplyContent("");
  };

  const startReply = ({ rootId, targetId, username }) => {
    setReplyingTo({ rootId, targetId, username });
    setReplyContent("");
    cancelCommentEdit();
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentContent("");
  };

  const editComment = async (id) => {
    if (!editingCommentContent.trim()) return;

    try {
      await api.put(`/blog/comments/${id}`, { content: editingCommentContent });
      cancelCommentEdit();
      fetchComments();
    } catch (err) {
      console.error(err);
      alert("Failed to edit comment");
    }
  };

  const openDeleteCommentDialog = (comment, type = "comment") => {
    setCommentToDelete({ ...comment, type });
  };

  const closeDeleteCommentDialog = () => {
    if (deletingCommentId) return;
    setCommentToDelete(null);
  };

  const deleteComment = async () => {
    if (!commentToDelete) return;

    try {
      setDeletingCommentId(commentToDelete.id);
      await api.delete(`/blog/comments/${commentToDelete.id}`);
      setCommentToDelete(null);
      if (editingCommentId === commentToDelete.id) {
        cancelCommentEdit();
      }
      fetchComments();
    } catch (err) {
      console.error(err);
      alert("Failed to delete comment");
    } finally {
      setDeletingCommentId(null);
    }
  };

  if (!post) return <div>Loading...</div>;

  const sanitizedContent = DOMPurify.sanitize(post.content || "");
  const commentFieldSx = {
    mb: 1,
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
      minWidth: 0,
      "& fieldset": { borderColor: "#f7b900" },
      "&:hover fieldset": { borderColor: "#f7b900" },
      "&.Mui-focused fieldset": { borderColor: "#f7b900" },
    },
    "& .MuiInputBase-inputMultiline": {
      overflowX: "hidden",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
      whiteSpace: "pre-wrap",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7a5a00" },
  };
  const yellowActionButtonSx = {
    "&&, &&.MuiButton-contained, &&.MuiButton-containedPrimary": {
      backgroundColor: "#f7b900 !important",
      backgroundImage: "none !important",
      border: "1px solid #f7b900 !important",
      color: "#1f1f1f !important",
      boxShadow: "none !important",
      textTransform: "none",
    },
    "&&:hover, &&.MuiButton-contained:hover, &&.MuiButton-containedPrimary:hover": {
      backgroundColor: "#d99f00 !important",
      backgroundImage: "none !important",
      borderColor: "#d99f00 !important",
      boxShadow: "none !important",
    },
  };
  const plainActionButtonSx = (hoverColor) => ({
    minWidth: 0,
    px: 0,
    color: "#6b7280",
    textTransform: "none",
    fontWeight: 700,
    "&:hover": { bgcolor: "transparent", color: hoverColor },
  });
  const commentCardSx = {
    flex: 1,
    minWidth: 0,
    px: 1,
    pt: 1,
    pb: 1,
    bgcolor: "#f2f4f7",
    border: "1px solid #e5e7eb",
    borderRadius: 2,
    boxShadow: "none",
  };
  const replyCardSx = {
    px: 1,
    pt: 1.25,
    pb: 0.75,
    borderLeft: "3px solid #f7b900",
    bgcolor: "#fff",
    borderRadius: 2,
  };
  const commentTextSx = {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  };

  const formatArticleDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };
  const formatRelativeTime = (value) => {
    if (!value) return "";
    const diffSeconds = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
    const units = [
      ["year", 31536000],
      ["month", 2592000],
      ["week", 604800],
      ["day", 86400],
      ["hour", 3600],
      ["minute", 60],
    ];
    const match = units.find(([, seconds]) => diffSeconds >= seconds);
    if (!match) return "just now";
    const [unit, seconds] = match;
    const count = Math.floor(diffSeconds / seconds);
    return `${count} ${unit}${count > 1 ? "s" : ""} ago`;
  };
  const wasEdited = (item) => {
    if (!item?.created_at || !item?.updated_at) return false;
    return new Date(item.updated_at).getTime() - new Date(item.created_at).getTime() > 60000;
  };
  const editedMeta = (item) =>
    wasEdited(item) ? `Edited ${formatRelativeTime(item.updated_at)}` : "";
  const getInitials = (value) => {
    const source = value || "User";
    return source
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");
  };
  const displayPersonName = (value) => {
    if (!value) return "User";
    const clean = String(value).trim();
    if (!clean.includes("@")) return clean;
    return clean
      .split("@")[0]
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part[0].toUpperCase() + part.slice(1))
      .join(" ");
  };
  const renderCommentLikeButton = (comment) => {
    const liked = Boolean(comment.liked_by_me);
    const count = Number(comment.like_count || 0);
    const likerNames = (comment.likers || []).map((liker) =>
      displayPersonName(liker.name)
    );
    const tooltipTitle = likerNames.length ? (
      <Box sx={{ py: 0.25, maxHeight: 220, overflowY: "auto" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>
          Liked by
        </Typography>
        {likerNames.map((name, index) => (
          <Typography key={comment.id + "-" + index} sx={{ fontSize: 12 }}>
            {name}
          </Typography>
        ))}
      </Box>
    ) : (
      "No likes yet"
    );

    return (
      <Tooltip title={tooltipTitle} arrow placement="top" enterDelay={250}>
        <span>
          <Button
            size="small"
            startIcon={
              liked ? <ThumbUpAltRoundedIcon /> : <ThumbUpOffAltRoundedIcon />
            }
            disabled={Boolean(likingCommentIds[comment.id])}
            aria-pressed={liked}
            onClick={() => toggleCommentLike(comment.id)}
            sx={{
              ...plainActionButtonSx("#d99f00"),
              ...(liked ? { color: "#d99f00" } : {}),
            }}
          >
            {liked ? "Liked" : "Like"}{count > 0 ? " " + count : ""}
          </Button>
        </span>
      </Tooltip>
    );
  };
  const readTime = Math.max(
    1,
    Math.ceil((post.content || "").replace(/<[^>]+>/g, " ").split(/\s+/).length / 220)
  );
  const commentCount =
    comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0) ||
    post.comments_count ||
    0;

  // Display name (αυτό που θα φαίνεται)
  const authorLabel =
    post?.author ||
    post?.author_name ||
    post?.author_full_name ||
    post?.author?.username ||
    post?.author?.email ||
    post.author_username ||
    authorUsername ||
    "Unknown";
  const authorDisplayName = displayPersonName(authorLabel);
  const authorProfilePath = (post.author_username || authorUsername)
    ? `/u/${encodeURIComponent(post.author_username || authorUsername)}`
    : null;
  const categoryStyle = getCategoryStyle(post.category);
  const categoryChipSx = {
    bgcolor: categoryStyle.background,
    color: categoryStyle.color,
    border: `1px solid ${categoryStyle.color}33`,
    fontWeight: 700,
  };
  const groupedEditCategories = BLOG_CATEGORY_GROUPS.map((group) => ({
    ...group,
    items: group.categories
      .map((name) => blogCategories.find((item) => item.name === name))
      .filter(Boolean),
  })).filter((group) => group.items.length);
  const ungroupedEditCategories = blogCategories.filter(
    (item) =>
      !BLOG_CATEGORY_GROUPS.some((group) => group.categories.includes(item.name))
  );

  // Link key (αυτό που θα χρησιμοποιεί το /u/:username)
  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/blog")}
            color="inherit"
            sx={{ minWidth: 0, px: 0, textTransform: "none", fontWeight: 700 }}
          >
            Back to posts
          </Button>

          {currentUser?.id === post.author_id && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={openEditPostDialog}
                sx={{
                  borderColor: "#e0e0e0",
                  color: "#1f1f1f",
                  textTransform: "none",
                  "&:hover": { borderColor: "#f7b900", bgcolor: "#fffaf0" },
                }}
              >
                Edit post
              </Button>
              <Button
                variant="contained"
                startIcon={<DeleteOutlineRoundedIcon />}
                color="error"
                onClick={() => setDeletePostDialogOpen(true)}
                sx={{
                  "&&": {
                    bgcolor: "#dc2626",
                    color: "#fff",
                    backgroundImage: "none",
                    border: "1px solid #dc2626",
                    boxShadow: "none",
                  },
                  textTransform: "none",
                  fontWeight: 700,
                  "&&:hover": { bgcolor: "#b91c1c", borderColor: "#b91c1c", boxShadow: "none" },
                }}
              >
                Delete post
              </Button>
            </Stack>
          )}
        </Stack>

        <Divider sx={{ mb: 5, borderColor: "#e5e7eb" }} />

        <Box sx={{ maxWidth: 760, mb: 6 }}>
          {/* <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 3, flexWrap: "wrap" }}>
            {post.category && (
              <Chip
                label={post.category}
                size="small"
                sx={categoryChipSx}
              />
            )}
            {post.date && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary", fontSize: 13 }}>
                <CalendarTodayRoundedIcon sx={{ fontSize: 16 }} />
                <span>{formatArticleDate(post.date)}</span>
              </Stack>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary", fontSize: 13 }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
              <span>{readTime} min read</span>
            </Stack>
          </Stack> */}

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 36, md: 50 },
              lineHeight: 1.08,
              fontWeight: 600,
              letterSpacing: 0,
              mb: 2.5,
            }}
          >
            {post.title}
          </Typography>

          {post.excerpt && (
            <Typography sx={{ color: "text.secondary", fontSize: 17, lineHeight: 1.7, mb: 4 }}>
              {post.excerpt}
            </Typography>
          )}

          {/* <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={post.author_avatar || undefined}
              sx={{ bgcolor: "#f7b900", color: "#4f3a00", fontWeight: 800 }}
            >
              {getInitials(authorDisplayName)}
            </Avatar>
            <Box>
              {authorProfilePath ? (
                <Link
                  component={RouterLink}
                  to={authorProfilePath}
                  underline="none"
                  sx={{ color: "#111", fontWeight: 800, textDecoration: "none", "&:hover": { textDecoration: "none" } }}
                >
                  {authorDisplayName}
                </Link>
              ) : (
                <Typography sx={{ fontWeight: 800 }}>{authorDisplayName}</Typography>
              )}
            </Box>
          </Stack> */}
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{
            mb: 4,
            py: 2,
            borderTop: "1px solid #ededed",
            borderBottom: "1px solid #ededed",
            color: "text.secondary",
            fontSize: 13,
          }}
        >
          {/* {post.category && <Chip label={post.category} size="small" sx={categoryChipSx} />} */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CalendarTodayRoundedIcon sx={{ fontSize: 16 }} />
            <span>{formatArticleDate(post.date)}</span>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
            <span>{readTime} min read</span>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />
            <span>{commentCount} comments</span>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 700px) 290px" },
            gap: { xs: 4, md: 6 },
            alignItems: "start",
          }}
        >
          <Box>
            {post.image && (
              <Box
                component="img"
                src={post.image}
                alt={post.title}
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: 1,
                  border: "1px solid #ededed",
                  mb: 3,
                }}
              />
            )}

            {post.tags && post.tags.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, mb: 4 }}>
                {post.tags.map((tag, i) => (
                  <Chip
                    key={i}
                    icon={<LocalOfferOutlinedIcon />}
                    label={tag}
                    size="small"
                    sx={{ bgcolor: "#fafafa", border: "1px solid #ededed" }}
                  />
                ))}
              </Stack>
            )}

            {/* CONTENT */}
            <Box
              sx={{
                mb: 6,
                color: "#151515",
                fontSize: 17,
                lineHeight: 1.8,
                overflowWrap: "anywhere",
                "& img": { maxWidth: "100%", height: "auto", borderRadius: 1 },
                "& p:first-of-type": { mt: 0 },
                "& p:last-of-type": { mb: 0 },
                "& h1, & h2, & h3": {
                  mt: 4,
                  mb: 1.25,
                  pb: 1,
                  borderBottom: "1px solid #e7e7e7",
                  lineHeight: 1.25,
                },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            <Divider sx={{ my: 4 }} />

            {/* COMMENTS */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <ChatBubbleOutlineRoundedIcon sx={{ color: "#f7b900" }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Comments
              </Typography>
              <Chip label={commentCount} size="small" sx={{ bgcolor: "#f7f7f7", fontWeight: 700 }} />
            </Stack>
            {comments.map((c) => {
              const replies = Array.isArray(c.replies) ? c.replies : [];
              const isExpanded = Boolean(expandedReplyThreads[c.id]);
              const visibleReplies = isExpanded ? replies : replies.slice(0, 1);
              const hiddenReplyCount = Math.max(0, replies.length - visibleReplies.length);

              return (
                <Box key={c.id} sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
                  <Avatar
                    src={c.author_avatar || undefined}
                    sx={{ color: "#000", bgcolor: "#f7b900", width: 40, height: 40, fontSize: 14, fontWeight: 800 }}
                  >
                    {getInitials(displayPersonName(c.author_name || c.author_username))}
                  </Avatar>
                  <Card
                    id={`comment-${c.id}`}
                    sx={{
                      ...commentCardSx,
                      animation: highlightedCommentId === c.id ? "commentFlash 900ms ease-in-out 3" : "none",
                      "@keyframes commentFlash": {
                        "0%, 100%": { backgroundColor: "#f2f4f7" },
                        "50%": { backgroundColor: "#fff3bf" },
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                        {displayPersonName(c.author_name || c.author_username)}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: 12, whiteSpace: "nowrap" }}>
                        {formatRelativeTime(c.created_at)}
                      </Typography>
                    </Stack>
                    {editingCommentId === c.id ? (
                      <Box sx={{ mt: 1, mb: 1.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Edit comment"
                          value={editingCommentContent}
                          onChange={(e) => setEditingCommentContent(e.target.value)}
                          multiline
                          minRows={2}
                          autoFocus
                          sx={commentFieldSx}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => editComment(c.id)}
                            sx={yellowActionButtonSx}
                          >
                            Save
                          </Button>
                          <Button size="small" onClick={cancelCommentEdit}>
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Typography sx={{ ...commentTextSx, mt: 0.5, mb: 0.5 }}>
                        {c.content}
                      </Typography>
                    )}

                    {editingCommentId !== c.id && (
                      <>
                        <Divider sx={{ mt: 0.75, mb: 0.25, borderColor: "rgba(107, 114, 128, 0.18)" }} />
                        <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: replies.length ? 1 : 0 }}
                      >
                        <Stack direction="row" spacing={1}>
                          {renderCommentLikeButton(c)}
                          {currentUser && (
                            <Button
                              size="small"
                              startIcon={<ReplyRoundedIcon />}
                              sx={plainActionButtonSx("#d99f00")}
                              onClick={() => {
                                if (replyingTo?.targetId === c.id) {
                                  setReplyingTo(null);
                                  setReplyContent("");
                                } else {
                                  startReply({
                                    rootId: c.id,
                                    targetId: c.id,
                                    username: displayPersonName(c.author_name || c.author_username),
                                  });
                                }
                              }}
                            >
                              Reply
                            </Button>
                          )}

                          {currentUser?.username === c.author_username && (
                            <>
                              <Button
                                size="small"
                                startIcon={<EditOutlinedIcon />}
                                sx={plainActionButtonSx("#2563eb")}
                                onClick={() => startCommentEdit(c.id, c.content)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                sx={plainActionButtonSx("#dc2626")}
                                onClick={() => openDeleteCommentDialog(c, "comment")}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </Stack>

                        {editedMeta(c) && (
                          <Typography
                            sx={{
                              color: "rgba(107, 114, 128, 0.72)",
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {editedMeta(c)}
                          </Typography>
                        )}
                        </Stack>
                      </>
                    )}

                    {replyingTo?.targetId === c.id && (
                      <Box sx={{ mb: 1.5, ml: { xs: 0, sm: 3 } }}>
                        <TextField
                          autoFocus
                          inputRef={replyInputRef}
                          fullWidth
                          size="small"
                          label={`Reply to ${replyingTo.username}`}
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          multiline
                          minRows={2}
                          sx={commentFieldSx}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => submitComment(c.id, replyingTo.targetId)}
                            sx={yellowActionButtonSx}
                          >
                            Post Reply
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyContent("");
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    )}

                  {replies.length > 0 && (
                      <Stack spacing={1} sx={{ mt: 1, ml: { xs: 0, sm: 3 } }}>
                        {visibleReplies.map((reply) => (
                          <Box
                            key={reply.id}
                            sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}
                          >
                            <Avatar
                              src={reply.author_avatar || undefined}
                              sx={{ color: "#000", bgcolor: "#f7b900", width: 32, height: 32, fontSize: 12, fontWeight: 800, flexShrink: 0 }}
                            >
                              {getInitials(displayPersonName(reply.author_name || reply.author_username))}
                            </Avatar>
                            <Box
                              id={`comment-${reply.id}`}
                              sx={{
                                ...replyCardSx,
                                flex: 1,
                                minWidth: 0,
                                animation: highlightedCommentId === reply.id ? "commentFlash 900ms ease-in-out 3" : "none",
                                "@keyframes commentFlash": {
                                  "0%, 100%": { backgroundColor: "#fff" },
                                  "50%": { backgroundColor: "#fff3bf" },
                                },
                              }}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                  {displayPersonName(reply.author_name || reply.author_username)}
                                </Typography>
                                <Typography sx={{ color: "text.secondary", fontSize: 12, whiteSpace: "nowrap" }}>
                                  {formatRelativeTime(reply.created_at)}
                                </Typography>
                              </Stack>
                              {(reply.reply_to_name || reply.reply_to_username) && (
                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    color: "grey.400",
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}
                                >
                                  Replying to {displayPersonName(reply.reply_to_name || reply.reply_to_username)}
                                </Typography>
                              )}
                              {editingCommentId === reply.id ? (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Edit reply"
                                    value={editingCommentContent}
                                    onChange={(e) =>
                                      setEditingCommentContent(e.target.value)
                                    }
                                    multiline
                                    minRows={2}
                                    autoFocus
                                    sx={commentFieldSx}
                                  />
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => editComment(reply.id)}
                                      sx={yellowActionButtonSx}
                                    >
                                      Save
                                    </Button>
                                    <Button size="small" onClick={cancelCommentEdit}>
                                      Cancel
                                    </Button>
                                  </Stack>
                                </Box>
                              ) : (
                                <Typography sx={{ ...commentTextSx, mt: 0.5, mb: 0.5, fontSize: 14 }}>
                                  {reply.content}
                                </Typography>
                              )}

                            {editingCommentId !== reply.id && (
                              <>
                                <Divider sx={{ mt: 0.75, mb: 0.25, borderColor: "rgba(107, 114, 128, 0.18)" }} />
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1}>
                                  {renderCommentLikeButton(reply)}
                                  {currentUser && (
                                    <Button
                                      size="small"
                                      startIcon={<ReplyRoundedIcon />}
                                      sx={plainActionButtonSx("#d99f00")}
                                      onClick={() => {
                                        if (replyingTo?.targetId === reply.id) {
                                          setReplyingTo(null);
                                          setReplyContent("");
                                        } else {
                                          startReply({
                                            rootId: c.id,
                                            targetId: reply.id,
                                            username: displayPersonName(reply.author_name || reply.author_username),
                                          });
                                        }
                                      }}
                                    >
                                      Reply
                                    </Button>
                                  )}

                                  {currentUser?.username === reply.author_username && (
                                    <>
                                      <Button
                                        size="small"
                                        startIcon={<EditOutlinedIcon />}
                                        sx={plainActionButtonSx("#2563eb")}
                                        onClick={() =>
                                          startCommentEdit(reply.id, reply.content)
                                        }
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        startIcon={<DeleteOutlineRoundedIcon />}
                                        sx={plainActionButtonSx("#dc2626")}
                                        onClick={() =>
                                          openDeleteCommentDialog(reply, "reply")
                                        }
                                      >
                                        Delete
                                      </Button>
                                    </>
                                  )}
                                </Stack>

                                {editedMeta(reply) && (
                                  <Typography
                                    sx={{
                                      color: "rgba(107, 114, 128, 0.72)",
                                      fontSize: 11,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {editedMeta(reply)}
                                  </Typography>
                                )}
                                </Stack>
                              </>
                            )}

                            {replyingTo?.targetId === reply.id && (
                              <Box sx={{ mt: 1.5 }}>
                                <TextField
                                  autoFocus
                                  inputRef={replyInputRef}
                                  fullWidth
                                  size="small"
                                  label={`Reply to ${replyingTo.username}`}
                                  value={replyContent}
                                  onChange={(e) => setReplyContent(e.target.value)}
                                  multiline
                                  minRows={2}
                                  sx={commentFieldSx}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => submitComment(c.id, replyingTo.targetId)}
                                    sx={yellowActionButtonSx}
                                  >
                                    Post Reply
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setReplyContent("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </Box>
                            )}
                            </Box>
                          </Box>
                        ))}
                        {hiddenReplyCount > 0 && (
                          <Button
                            size="small"
                            onClick={() =>
                              setExpandedReplyThreads((prev) => ({ ...prev, [c.id]: true }))
                            }
                            sx={plainActionButtonSx("#d99f00")}
                          >
                            View {hiddenReplyCount} more {hiddenReplyCount === 1 ? "reply" : "replies"}
                          </Button>
                        )}
                        {isExpanded && replies.length > 1 && (
                          <Button
                            size="small"
                            onClick={() =>
                              setExpandedReplyThreads((prev) => ({ ...prev, [c.id]: false }))
                            }
                            sx={plainActionButtonSx("#6b7280")}
                          >
                            Show less
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Card>
                </Box>
              );
            })}

            {/* ADD COMMENT */}
            {currentUser ? (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mt: 4, width: "100%", minWidth: 0 }}
              >
                <Avatar sx={{ bgcolor: "#fff", color: "text.secondary", border: "1px solid #e6e6e6" }}>
                  <PersonOutlineRoundedIcon />
                </Avatar>
                <TextField
                  label="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  multiline
                  maxRows={5}
                  sx={{
                    ...commentFieldSx,
                    flex: "1 1 0",
                    width: "auto",
                  }}
                />

                <Button
                  variant="contained"
                  onClick={() => submitComment()}
                  sx={{
                    ...yellowActionButtonSx,
                    minWidth: 48,
                    height: 48,
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  <SendRoundedIcon />
                </Button>
              </Stack>
            ) : (
              <Typography sx={{ mt: 3 }}>You must be logged in to comment.</Typography>
            )}

          </Box>

          <Stack spacing={2.5} sx={{ position: { md: "sticky" }, top: { md: 92 } }}>
            <Card sx={{ p: 2, border: "1px solid #e5e5e5", boxShadow: "none" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, mb: 2 }}>
                AUTHOR
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  src={post.author_avatar || undefined}
                  sx={{ bgcolor: "#fff7d6", color: "#b18400", fontWeight: 800 }}
                >
                  {getInitials(authorDisplayName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  {authorProfilePath ? (
                    <Link
                      component={RouterLink}
                      to={authorProfilePath}
                      underline="none"
                      sx={{ color: "#111", fontWeight: 800, fontSize: 14, textDecoration: "none", "&:hover": { textDecoration: "none" } }}
                    >
                      {authorDisplayName}
                    </Link>
                  ) : (
                    <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{authorDisplayName}</Typography>
                  )}
                </Box>
              </Stack>
            </Card>

            {post.category && (
              <Card sx={{ p: 2, border: "1px solid #e5e5e5", boxShadow: "none" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, mb: 1.5 }}>
                  CATEGORY
                </Typography>
                <Chip
                  component={RouterLink}
                  to={`/blog?category=${encodeURIComponent(post.category)}`}
                  clickable
                  label={post.category}
                  size="small"
                  sx={{
                    ...categoryChipSx,
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: categoryStyle.background,
                      boxShadow: `0 0 0 1px ${categoryStyle.color}44`,
                    },
                  }}
                />
              </Card>
            )}

            {moreArticles.length > 0 && (
              <Card sx={{ p: 2, border: "1px solid #e5e5e5", boxShadow: "none" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, mb: 1.5 }}>
                  MORE ARTICLES
                </Typography>
                <Stack spacing={1.5}>
                  {moreArticles.map((article) => (
                    <Box key={article.id || article.slug}>
                      <Link
                        component={RouterLink}
                        to={`/blog/${article.id || article.slug}`}
                        underline="none"
                        sx={{
                          color: "#111",
                          fontWeight: 800,
                          fontSize: 13,
                          lineHeight: 1.35,
                          display: "block",
                          underline: "hover",
                          "&::before": { display: "none" },
                        }}
                      >
                        {article.title}
                      </Link>
                      <Typography sx={{ color: "text.secondary", fontSize: 11, mt: 0.5 }}>
                        {formatArticleDate(article.date)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>
        </Box>

        <Dialog
          fullScreen
          open={editDialogOpen}
          onClose={() => {
            if (!editLoading) setEditDialogOpen(false);
          }}
          TransitionComponent={EditPostDialogTransition}
        >
          <AppBar
            elevation={0}
            sx={{
              position: "sticky",
              bgcolor: "#fff",
              color: "#111",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Toolbar sx={{ gap: 1.5 }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setEditDialogOpen(false)}
                disabled={editLoading}
                aria-label="close"
              >
                <CloseRoundedIcon />
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 } }}>
                  Edit post
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 13 }} noWrap>
                  Update the article details, image and content.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={
                  editLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveRoundedIcon />
                  )
                }
                onClick={savePostEdit}
                disabled={editLoading}
                sx={yellowActionButtonSx}
              >
                Save changes
              </Button>
            </Toolbar>
          </AppBar>

          <DialogContent sx={{ bgcolor: "#fcfcfc", p: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: 1180, mx: "auto" }}>
              {editError && (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {editError}
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2.25fr) 1fr" },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <Paper variant="outlined" sx={editPanelSx}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                    Content Editor
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 3 }}>
                    Refine the title, summary and article body before saving.
                  </Typography>

                  <Stack spacing={2.75}>
                    <TextField
                      label="Post Title"
                      required
                      fullWidth
                      value={editTitle}
                      onChange={(event) => setEditTitle(event.target.value)}
                      sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
                    />
                    <TextField
                      label="Short Excerpt"
                      fullWidth
                      multiline
                      minRows={3}
                      value={editExcerpt}
                      onChange={(event) => setEditExcerpt(event.target.value)}
                      helperText="A short summary shown on article cards and under the title."
                      sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                        Main Content
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: "#fff",
                          "& .ql-container": {
                            borderColor: "#f7b900",
                            height: { xs: 360, md: 500 },
                            borderBottomLeftRadius: 8,
                            borderBottomRightRadius: 8,
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: 14,
                          },
                          "& .ql-editor": {
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            WebkitOverflowScrolling: "touch",
                            "& img, & iframe, & video": {
                              maxWidth: "100%",
                              height: "auto",
                            },
                          },
                          "& .ql-toolbar": {
  borderColor: "#f7b900",
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,

  "& button:hover .ql-stroke, & button.ql-active .ql-stroke": {
    stroke: "#f7b900",
  },

  "& button:hover .ql-fill, & button.ql-active .ql-fill": {
    fill: "#f7b900",
  },

  "& .ql-picker-label:hover, & .ql-picker-label.ql-active": {
    color: "#f7b900",
  },

  "& .ql-picker-label:hover .ql-stroke, & .ql-picker-label.ql-active .ql-stroke": {
    stroke: "#f7b900",
  },
},
                        }}
                      >
                        <ReactQuill
                          theme="snow"
                          value={editContent}
                          onChange={setEditContent}
                          modules={BlogPostDetail.modules}
                          formats={BlogPostDetail.formats}
                          placeholder="Update your article here..."
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Paper>

                <Stack spacing={2}>
                  <Paper variant="outlined" sx={editPanelSx}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                      Details
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 2 }}>
                      Keep the article discoverable with a clear category and tags.
                    </Typography>
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small" sx={authFieldSx}>
                        <InputLabel id="edit-post-category-label">Category</InputLabel>
                        <Select
                          labelId="edit-post-category-label"
                          value={editCategory}
                          label="Category"
                          IconComponent={KeyboardArrowDownRoundedIcon}
                          MenuProps={selectMenuProps}
                          onChange={(event) => setEditCategory(event.target.value)}
                        >
                          {groupedEditCategories.flatMap((group) => [
                            <ListSubheader key={`${group.label}-header`}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: group.color,
                                  }}
                                />
                                <span>{group.label}</span>
                              </Stack>
                            </ListSubheader>,
                            ...group.items.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.name}
                              </MenuItem>
                            )),
                          ])}
                          {ungroupedEditCategories.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Autocomplete
                        multiple
                        freeSolo
                        popupIcon={<KeyboardArrowDownRoundedIcon />}
                        componentsProps={{ paper: tagAutocompletePaperProps }}
                        options={blogTags}
                        value={editTags}
                        getOptionLabel={(option) => option.name ?? option}
                        isOptionEqualToValue={(option, value) =>
                          option.id && value.id
                            ? option.id === value.id
                            : option.name === value.name
                        }
                        onChange={(_, values) => {
                          const normalized = values
                            .map((item) =>
                              typeof item === "string"
                                ? { id: null, name: item.trim() }
                                : item
                            )
                            .filter((item) => item.name);
                          setEditTags(normalized);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tags"
                            size="small"
                            helperText="Select existing tags or type a new one."
                            sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((item, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={`${item.id || "new"}-${item.name}`}
                              label={item.name}
                              size="small"
                            />
                          ))
                        }
                      />
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={editPanelSx}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      Featured Image
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5, mb: 2 }}>
                      Replace the visual that appears inside the article and on blog cards.
                      Maximum file size: 100 KB.
                    </Typography>
                    <Box sx={{ position: "relative", mb: 1.5 }}>
                      <Box
                        component="img"
                        src={
                          editImagePreview ||
                          (!editImageRemoved && editExistingImageUrl) ||
                          BLOG_FALLBACK_IMAGE
                        }
                        alt="Post cover preview"
                        sx={{
                          width: "100%",
                          height: 170,
                          objectFit: "cover",
                          display: "block",
                          borderRadius: 1.5,
                          border: "1px solid #ededed",
                        }}
                      />
                      {(editImagePreview || (!editImageRemoved && editExistingImageUrl)) && (
                        <IconButton
                          size="small"
                          aria-label="Remove featured image"
                          title="Remove featured image"
                          onClick={() => {
                            setEditFeaturedImage(null);
                            setEditImageRemoved(true);
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            width: 30,
                            height: 30,
                            bgcolor: "#fff",
                            color: "#dc2626",
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
                            "&:hover": { bgcolor: "#fff1f2", color: "#b91c1c" },
                          }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadOutlinedIcon />}
                      fullWidth
                      sx={{ textTransform: "none" }}
                    >
                      {editFeaturedImage ? "Change image" : "Upload file"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (imageExceedsUploadLimit(file)) {
                            event.target.value = "";
                            setEditError(IMAGE_UPLOAD_SIZE_ERROR);
                            return;
                          }
                          setEditFeaturedImage(file);
                          if (file) setEditImageRemoved(false);
                        }}
                      />
                    </Button>
                    {editFeaturedImage && (
                      <Typography color="text.secondary" noWrap sx={{ fontSize: 11, mt: 1 }}>
                        {editFeaturedImage.name}
                      </Typography>
                    )}
                  </Paper>
                </Stack>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog
          open={deletePostDialogOpen}
          title="Do you want to delete this post?"
          description={`Are you sure you want to delete "${post.title}"? All comments and replies on this post will also be deleted. This action cannot be undone.`}
          loading={deletingPost}
          onClose={() => {
            if (!deletingPost) setDeletePostDialogOpen(false);
          }}
          onConfirm={deletePost}
        />

        <DeleteConfirmDialog
          open={Boolean(commentToDelete)}
          title={`Do you want to delete this ${commentToDelete?.type === "reply" ? "reply" : "comment"}?`}
          description={`Are you sure you want to delete this ${commentToDelete?.type === "reply" ? "reply" : "comment"}?${commentToDelete?.type !== "reply" ? " Its replies will also be deleted." : ""} This action cannot be undone.`}
          loading={Boolean(deletingCommentId)}
          onClose={closeDeleteCommentDialog}
          onConfirm={deleteComment}
        />

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
    </Box>
  );
}

BlogPostDetail.modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};

BlogPostDetail.formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "ordered",
  "link",
  "image",
];
